import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { clone, includes, omit } from 'lodash-es';
import {
    FormPageTypeEnum,
    FormSettingsEnum,
    PreviewPageType,
    ProductCode,
    TemplateScopeType,
    TemplateType,
} from '@dev/ee-api/ee-api-models';
import { Typography } from '@dev/ee-components';
import { StartLoadingTemplates } from '@/store/templateList/actions';
import { ModifyForm, getFormCreatorStore, SetFormErrors } from '@/store/formCreator/actions';
import {
    StartLoadingSubmitLandingPages,
    StartLoadingTemplateParams,
    StartLoadingSubmitTemplate,
    getLandingPageList,
} from '@/store/landingPageList/actions';
import { PageTypeEnum } from '@/store/landingPageList/types';
import { checkIsUrlInvalid } from '@/services/tools/urlValidation/checkIsUrlInvalid';
import { FormErrorNames } from '@/store/formCreator/types';
import { LockIcon } from '@/services/icons/LockedIcon';
import { Divider } from '@/assets/Divider';
import { SavedLockedInfo as DoubleOptInUpselling } from '@/components/TemplateEditor/components/SavedLockedInfo';
import { useCurrentPlanStatus } from '@/services/hooks/useCurrentPlanStatus';
import { BillingPaths, pathsParams } from '@/services/dictionaries/Paths';
import { usePageTypeValue } from './hooks/usePageValue';
import { DoubleOptInSection } from './components/DoubleOptInSection';
import { DisplayedScreenEnum, FormPageDropdownSection } from './components/FormPageDropdown';
import { AlreadySubscribedDropdown } from './components/AlreadySubscribedDropdown';

export const DEFAULT_OPTIONS_COUNT = 10;

export const Step3EditContent = (): JSX.Element => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { formObject, formPayload, errors } = useSelector(getFormCreatorStore);
    const { templateFormSubmit, submitLandingPagesList } = useSelector(getLandingPageList);
    const { isPilotPlan } = useCurrentPlanStatus();
    const formSubmitPage = formPayload?.FormSubmitPage ?? formObject?.FormSubmitPage;
    const [chosenLandingPage, setChosenLandingPage] = useState<any>([]);
    const [landingPageSearchSubmitForm, setLandingPageSearchSubmitForm] = useState(null);
    const [externalWebsite, setExternalWebsite] = useState(!checkIsUrlInvalid(formSubmitPage) ? formSubmitPage : null);
    const [isExternalWebsiteError, setExternalWebsiteError] = useState(false);

    const submitPagesParams: StartLoadingTemplateParams = {
        page: 1,
        search: landingPageSearchSubmitForm,
        limit: DEFAULT_OPTIONS_COUNT,
        type: [TemplateScopeType.Personal],
        templateTypes: [TemplateType.LandingPageEditor,TemplateType.LandingPageTemplate],
        isInfinityScroll: true,
        pageType: PageTypeEnum.FormSubmit,
    };

    useEffect((): void => {
        const formSettings = formPayload?.FormSettings ?? formObject?.FormSettings ?? [];
        dispatch(
            StartLoadingTemplates({
                page: 1,
                limit: DEFAULT_OPTIONS_COUNT,
                type: [TemplateScopeType.Personal],
                templateTypes: [TemplateType.TemplateEditor, TemplateType.RawHTML, TemplateType.DragDropEditor],
            })
        );
        dispatch(StartLoadingSubmitLandingPages(submitPagesParams));

        dispatch(
            ModifyForm({
                FormSubmitPage: formObject?.FormSubmitPage || null,
                FormAlreadySubscribedPage: formObject?.FormAlreadySubscribedPage || null,
                FormSettings: formObject?.FormSettings || null,
                ...(isPilotPlan
                    ? {
                          FormSettings: [...formSettings, FormSettingsEnum.DoubleOptInDisabled],
                          DoubleOptInPage: null,
                          ActivationTemplateName: null,
                      }
                    : {}),
            })
        );
        if (
            includes(formObject?.FormPageType, FormPageTypeEnum.LandingPageFormSubmitPage) &&
            !!formObject?.FormSubmitPage
        ) {
            dispatch(StartLoadingSubmitTemplate(formObject?.FormSubmitPage));
        }
    }, []);

    useEffect((): void => {
        setChosenLandingPage({
            label: templateFormSubmit?.Name,
            value: templateFormSubmit?.Name,
            link: templateFormSubmit?.Link,
        });
    }, [templateFormSubmit]);

    const errorName = FormErrorNames.FORM_SUBMIT_INVALID_URL;

    const resetError = (): void => {
        const errorsObject = clone(errors) || {};
        const newErrorsObject = omit(errorsObject, errorName);
        dispatch(SetFormErrors(newErrorsObject));
    };

    useEffect(() => {
        const errorsObject = clone(errors) || {};

        if (isExternalWebsiteError) {
            errorsObject[errorName] = true;
            dispatch(SetFormErrors(errorsObject));
            return;
        }
        resetError();
    }, [isExternalWebsiteError]);

    const changeFormSubmitPage = (ev, obj): void => {
        const formPageType = formPayload?.FormPageType ?? formObject?.FormPageType;
        const newFormPageType = [...formPageType];
        let newFormSubmitPage = null;

        if (obj.value === DisplayedScreenEnum.Defaultpage) {
            newFormPageType[1] = FormPageTypeEnum.DefaultFormSubmitPage;
        } else if (obj.value === DisplayedScreenEnum.Landingpage) {
            newFormPageType[1] = FormPageTypeEnum.LandingPageFormSubmitPage;
            if (submitLandingPagesList.length) {
                setChosenLandingPage({
                    label: submitLandingPagesList[0]?.Name,
                    value: submitLandingPagesList[0]?.Name,
                    link: submitLandingPagesList[0]?.Link,
                });
                newFormSubmitPage = submitLandingPagesList[0]?.Name;
            }
        } else {
            newFormPageType[1] = FormPageTypeEnum.ExternalWebsiteFormSubmitPage;
        }
        setExternalWebsite(null);
        resetError();
        dispatch(ModifyForm({ FormPageType: newFormPageType, FormSubmitPage: newFormSubmitPage }));
    };

    const clickUpgradeBtn = (): void => {
        window.location.href = `${location.origin}${BillingPaths.BillingPlanList.replace(
            pathsParams.productCode,
            ProductCode.Marketing.toLowerCase()
        )}`;
    };

    return (
        <>
            {isPilotPlan ? (
                <DoubleOptInUpselling
                    isSidebar={false}
                    isFullWidth
                    title={{ text: t('doubleoptinconfirmation'), icon: <LockIcon width={15} height={15} /> }}
                    body={{ subtitle: t('doubleoptinupselling_subtitle'), text: t('doubleoptinupselling_desc') }}
                    button={{
                        startIcon: <LockIcon width={20} height={20} />,
                        text: t('upgradetounlock'),
                        onClick: clickUpgradeBtn,
                    }}
                />
            ) : (
                <DoubleOptInSection />
            )}
            <Divider marginTop={32} marginBottom={32} />
            <Typography variant="h3">{t('contactsubmitsform')}</Typography>
            <FormPageDropdownSection
                pageTemplateType={PreviewPageType.FormSubmit}
                idPageDropdown="after-submit"
                idExternalWebsiteInput="after-submit"
                label={t('displayscreenaftercontactsubmitform')}
                onDropdownPageClick={changeFormSubmitPage}
                pageType={usePageTypeValue(PreviewPageType.FormSubmit)}
                externalWebsiteValue={externalWebsite}
                setExternalWebsite={setExternalWebsite}
                isExternalWebsiteError={isExternalWebsiteError}
                setExternalWebsiteError={setExternalWebsiteError}
                idLandingPage="after-submit"
                chosenLandingPage={chosenLandingPage}
                setChosenLandingPage={setChosenLandingPage}
                landingPageSearch={landingPageSearchSubmitForm}
                setLandingPageSearch={setLandingPageSearchSubmitForm}
                landingPagesParams={submitPagesParams}
            />
            <AlreadySubscribedDropdown />
        </>
    );
};
